import React, { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckOutlined } from '@ant-design/icons';

const COLORS = ['#22c55e', '#4ade80', '#86efac', '#facc15', '#fde047', '#fef08a', '#fff'];

const getRandom = (min, max) => min + Math.random() * (max - min);

/**
 * Burst of sparkle particles + checkmark for task completion.
 * - inline: same place/size as green tick (top-right of card, 22px). Use for card-level celebration.
 * - fullScreen: center of viewport (default off when inline).
 */
export function CompletionCelebration({ play, onComplete, inline = false }) {
    const tickSize = inline ? 22 : 56;
    const tickIconSize = inline ? 12 : 28;

    const particles = useMemo(() => {
        const count = inline ? 16 : 48;
        const distMin = inline ? 12 : 60;
        const distMax = inline ? 28 : 140;
        const sizeMin = inline ? 2 : 4;
        const sizeMax = inline ? 5 : 10;
        const durMin = inline ? 0.5 : 0.8;
        const durMax = inline ? 0.9 : 1.4;
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            angle: (360 / count) * i + getRandom(0, 15),
            distance: getRandom(distMin, distMax),
            size: getRandom(sizeMin, sizeMax),
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            duration: getRandom(durMin, durMax),
            delay: getRandom(0, inline ? 0.08 : 0.15),
        }));
    }, [inline]);

    useEffect(() => {
        if (!play) return;
        const ms = inline ? 1200 : 2200;
        const t = setTimeout(() => onComplete?.(), ms);
        return () => clearTimeout(t);
    }, [play, onComplete, inline]);

    const wrapperStyle = inline
        ? {
              position: 'absolute',
              top: 10,
              right: 10,
              width: 22,
              height: 22,
              overflow: 'visible',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
          }
        : {
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
          };

    const centerStyle = inline
        ? { position: 'absolute', left: 11, top: 11, width: 2, height: 2, marginLeft: -1, marginTop: -1 }
        : { position: 'absolute', left: '50%', top: '50%', width: 20, height: 20, marginLeft: -10, marginTop: -10 };

    return (
        <AnimatePresence>
            {play && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={wrapperStyle}
                    aria-hidden
                >
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 20, duration: 0.35 }}
                        style={{
                            position: 'absolute',
                            left: inline ? 11 : '50%',
                            top: inline ? 11 : '50%',
                            width: tickSize,
                            height: tickSize,
                            marginLeft: inline ? -tickSize / 2 : -tickSize / 2,
                            marginTop: inline ? -tickSize / 2 : -tickSize / 2,
                            borderRadius: '50%',
                            background: '#22c55e',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: inline
                                ? '0 0 0 2px rgba(34, 197, 94, 0.4)'
                                : '0 0 0 4px rgba(34, 197, 94, 0.3), 0 4px 20px rgba(34, 197, 94, 0.4)',
                        }}
                    >
                        <CheckOutlined style={{ fontSize: tickIconSize }} />
                    </motion.div>
                    <div style={centerStyle}>
                        {particles.map((p) => {
                            const rad = (p.angle * Math.PI) / 180;
                            const x = Math.cos(rad) * p.distance;
                            const y = Math.sin(rad) * p.distance;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                                    animate={{ x, y, opacity: 0, scale: 0.3 }}
                                    transition={{
                                        duration: p.duration,
                                        delay: p.delay,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                    style={{
                                        position: 'absolute',
                                        left: '50%',
                                        top: '50%',
                                        width: p.size,
                                        height: p.size,
                                        marginLeft: -p.size / 2,
                                        marginTop: -p.size / 2,
                                        borderRadius: '50%',
                                        background: p.color,
                                        boxShadow: `0 0 ${p.size}px ${p.color}`,
                                    }}
                                />
                            );
                        })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default CompletionCelebration;
